import _ from 'lodash';
import { isEmptyVal } from '@/utils';
import {
  convertSoftBundleInfo2State,
  convertPureReferralCouponInfo2State,
} from '@/views/config/coupon/helper/result-to-state';
import { computePromotionDiscountInfoMap } from '@/views/config/coupon/helper/compute';
import { PROMOTION_TYPE, couponFetchStatus, PROMOTION_SCENARIO_TYPE } from '@/views/config/coupon/helper/constant';

export const PROMOTION_ACTION_TYPES = {
  initPromotionState: 'init/promotion/state',
};

/**
 * Pure function: Calculate promotion state based on partner, coupon, and free trial scenarios
 * @param {Object} params - Parameters object
 * @param {Object} params.partnerRefer - Partner referral object
 * @param {Object} params.couponState - Coupon state object
 * @returns {Object} Promotion state object
 * @returns {string} returns.scenarioType - Scenario type enum value
 * @returns {boolean} returns.isPendingCoupon - Whether coupon is pending
 * @returns {boolean} returns.hasPartner - Whether partner scenario exists
 * @returns {boolean} returns.hasCoupon - Whether coupon scenario exists
 * @returns {boolean} returns.hasFreeTrial - Whether free trial scenario exists
 */
export const calculatePromotionState = (params = {}) => {
  const {
    partnerRefer = null,
    couponState = {},
  } = params;

  // Check if partner scenario
  const hasPartner = !isEmptyVal(partnerRefer)

  // Check if coupon scenario
  const hasCoupon = !isEmptyVal(partnerRefer?.coupon);

  // Check if free trial scenario
  const hasFreeTrial = !isEmptyVal(window.$config?.('trialCampaignInfo'));

  // Check if coupon is pending
  const isPendingCoupon = couponState?.isPendingCoupon || false;

  // Determine scenario type
  let scenarioType = PROMOTION_SCENARIO_TYPE.NONE;

  if (hasPartner && hasCoupon && hasFreeTrial) {
    scenarioType = PROMOTION_SCENARIO_TYPE.PARTNER_COUPON_FREE_TRIAL;
  } else if (hasPartner && hasCoupon) {
    scenarioType = PROMOTION_SCENARIO_TYPE.PARTNER_COUPON;
  } else if (hasPartner && hasFreeTrial) {
    scenarioType = PROMOTION_SCENARIO_TYPE.PARTNER_FREE_TRIAL;
  } else if (hasCoupon && hasFreeTrial) {
    scenarioType = PROMOTION_SCENARIO_TYPE.COUPON_FREE_TRIAL;
  } else if (hasPartner) {
    scenarioType = PROMOTION_SCENARIO_TYPE.PARTNER_ONLY;
  } else if (hasCoupon) {
    scenarioType = PROMOTION_SCENARIO_TYPE.COUPON_ONLY;
  } else if (hasFreeTrial) {
    scenarioType = PROMOTION_SCENARIO_TYPE.FREE_TRIAL_ONLY;
  }

  return {
    scenarioType,
    isPendingCoupon,
    hasPartner,
    hasCoupon,
    hasFreeTrial,
  };
};

const state = {
  promotionInfo: {},
};

const getters = {
  promotionInfo: (state) => state.promotionInfo,
  allPromotionInfoStatusList: (state, getters) => {
    return _.chain(getters.promotionInfo)
      .values()
      .map(p => p.status)
      .value();
  },
  promotionDiscountInfoMap: (state, getters) => computePromotionDiscountInfoMap(getters.promotionInfo),
  promotionTotalDiscount: (state, getters) => {
    return _.chain(getters.promotionDiscountInfoMap)
      .values()
      .sumBy('totalDiscount')
      .value();
  },
  planTotalDiscount: (state, getters) => {
    return getters.promotionTotalDiscount + getters.discountWithAmountMap.totalDiscount;
  },
  /**
   * @description soft bundle plan configuration
   * @return {SoftBundleConfigBean}
   */
  softBundleConfiguration: (_, getters) => getters.partnerRefer?.features?.softBundleConfig,
  isExistSoftBundleConfig: (_, getters) => !isEmptyVal(getters.softBundleConfiguration),
  partnerUseInSoftBundle: (_, getters)=>  {
    return getters.partnerRefer?.features?.useInSoftBundle;
  },
  softBundleUseByPriceEngine: (state, getters) => {
    return _.get(getters.softBundleConfiguration, ['config', 'usePriceEngine'], false);
  },
  softBundleValidateByCoupon: (_, getters) => {
    return !isEmptyVal(getters.softBundleConfiguration) && getters.partnerRefer?.rules?.validateByCoupon;
  },
  softBundleState: (state, getters) => {
    const basicInfo = getters.promotionInfo?.[PROMOTION_TYPE.softBundle] || {};
    const discountInfo = getters.promotionDiscountInfoMap?.[PROMOTION_TYPE.softBundle] || {};
    const status = basicInfo?.status;
    const lastStatus = basicInfo?.lastStatus;
    const bundlePlanMap = basicInfo?.getBundlePlanMap?.();
    const bundlePriceList = getters.partnerRefer?.bundlePlanPriceList || [];

    return {
      status: status,
      lastStatus: lastStatus,
      basicInfo: basicInfo,
      discountInfo: discountInfo,
      isSuccess: status === couponFetchStatus.successfully,
      isPending: status === couponFetchStatus.pending,
      isFailure: status === couponFetchStatus.failure,
      isIdle: status === couponFetchStatus.idle,
      code: getters.partnerRefer?.jointsaleCode,
      bundleName: getters.softBundleConfiguration?.config?.bundleName,
      bundlePlanMap,
      bundlePriceList,
    };
  },
  promotionState: (state, getters) => {
    return calculatePromotionState({
      partnerRefer: getters.partnerRefer,
      couponState: getters.couponState,
    });
  },
};

const mutations = {
  addPromotionInfo(state, info) {
    state.promotionInfo[info.promotionType] = info;
  },
  removePromotionInfo(state, promotionType) {
    delete state.promotionInfo[promotionType];
  },
  updatePromotionInfoStatus(state, info) {
    const { promotionType, status, lastStatus } = info;

    if (!promotionType || !status || !state.promotionInfo[promotionType]) return;

    state.promotionInfo[promotionType] = {
      ...state.promotionInfo[promotionType] || {},
      status,
      lastStatus,
    };
  },
};

const actions = {
  [PROMOTION_ACTION_TYPES.initPromotionState](context) {
    const { commit, getters } = context;
    const { bundlePlanPriceList, features, rules, referralCouponCode } = getters.partnerRefer;

    // soft bundle with price engine to drive price change
    if (features?.useInSoftBundle && !isEmptyVal(bundlePlanPriceList)) {
      const promotionInfo = convertSoftBundleInfo2State({ bundlePlanPriceList }, context);
      commit('addPromotionInfo', promotionInfo);
    } else if (rules?.validateByReferralCoupon && !isEmptyVal(referralCouponCode)) {
      const promotionInfo = convertPureReferralCouponInfo2State(context);
      commit('addPromotionInfo', promotionInfo);
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
